import { logError } from '@/lib/errorLogging';

import { htmlToPlainText, renderMarkdown, sanitizeHtml } from './html';

const HTML_TAB_STOP = 8;

function textToLinePreservingHtml(text: string) {
  return text
    .split(/\r\n|\r|\n/)
    .map((line) => {
      let expandedLine = '';
      let column = 0;

      for (const character of line) {
        if (character === '\t') {
          // Expand to 8-column stops because pasted HTML cannot rely on CSS `tab-size`
          const spacesToTabStop = HTML_TAB_STOP - (column % HTML_TAB_STOP);
          expandedLine += ' '.repeat(spacesToTabStop);
          column += spacesToTabStop;
        } else {
          expandedLine += character;
          column += 1;
        }
      }

      return expandedLine
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;')
        .replace(/(^ +)|( +$)|( {2,})/g, (spaces) =>
          '&nbsp;'.repeat(spaces.length)
        );
    })
    .join('<br>');
}

export async function writeClipboardText(text: string, html: string) {
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]);
  } catch (error) {
    logError(
      error,
      'Failed to copy formatted text, falling back to plain text'
    );
    await navigator.clipboard.writeText(text);
  }
}

export async function copyTextWithLineBreaks(text: string) {
  await writeClipboardText(text, textToLinePreservingHtml(text));
}

export async function copyRenderedMarkdown(markdown: string) {
  const { html, text } = renderMarkdown(markdown);
  await writeClipboardText(text, html);
}

export async function copySelection() {
  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) return;

  let sanitizedText = '';

  try {
    const container = document.createElement('div');

    // Merge all selection ranges (e.g. Firefox ctrl+click multi-select)
    for (let i = 0; i < selection.rangeCount; i++) {
      container.appendChild(selection.getRangeAt(i).cloneContents());
    }

    if (!container.innerHTML.trim()) return;

    // Remove UI chrome from the cloned content
    const elementsToRemove = container.querySelectorAll(
      'button, [role="button"], .copy-button, .icon-button, .no-copy, [data-no-copy="true"], [aria-hidden="true"], .opacity-0, .group-hover\\:opacity-50'
    );
    elementsToRemove.forEach((el) => el.remove());

    const iconSvgs = container.querySelectorAll(
      'button svg, .icon-button svg, [role="button"] svg, svg.lucide-icon, svg[class*="icon"]'
    );
    iconSvgs.forEach((el) => el.remove());

    container
      .querySelectorAll('input, select, textarea')
      .forEach((el) => el.remove());

    const html = sanitizeHtml(container.innerHTML);

    sanitizedText = htmlToPlainText(html);

    // Bail out if sanitization stripped everything (e.g. selection was only UI chrome)
    if (!html.trim() && !sanitizedText.trim()) return;

    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([sanitizedText], { type: 'text/plain' }),
      });

      await navigator.clipboard.write([clipboardItem]);
    } else {
      // Save original selection so execCommand doesn't destroy it
      const savedRanges: Range[] = [];
      for (let i = 0; i < selection.rangeCount; i++) {
        savedRanges.push(selection.getRangeAt(i).cloneRange());
      }

      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'fixed';
      tempDiv.style.left = '-9999px';
      tempDiv.innerHTML = html;
      document.body.appendChild(tempDiv);

      const tempRange = document.createRange();
      tempRange.selectNodeContents(tempDiv);
      selection.removeAllRanges();
      selection.addRange(tempRange);

      const success = document.execCommand('copy');

      document.body.removeChild(tempDiv);

      // Restore original selection
      selection.removeAllRanges();
      savedRanges.forEach((r) => selection.addRange(r));

      if (!success) {
        throw new Error('Copy command failed');
      }
    }
  } catch (error) {
    logError(
      error,
      'Failed to copy formatted selection, falling back to plain text'
    );
    const plainText = sanitizedText || selection.toString();
    try {
      await navigator.clipboard.writeText(plainText);
    } catch {
      const tempTextArea = document.createElement('textarea');
      tempTextArea.value = plainText;
      tempTextArea.style.position = 'fixed';
      tempTextArea.style.left = '-9999px';
      document.body.appendChild(tempTextArea);
      tempTextArea.select();
      document.execCommand('copy');
      document.body.removeChild(tempTextArea);
    }
  }
}

function copiedTextFromHtml(html: string) {
  if (!html) return '';

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  parsed.body
    .querySelectorAll('style, script')
    .forEach((node) => node.remove());

  return parsed.body.textContent?.trim() ?? '';
}

export function shouldAttachClipboardFiles(clipboardData: DataTransfer | null) {
  if (!clipboardData) return false;

  const fileTypes = Array.from(clipboardData.items)
    .filter((item) => item.kind === 'file')
    .map((item) => item.type);

  if (fileTypes.length === 0) return false;
  if (!fileTypes.every((type) => type.startsWith('image/'))) return true;

  return copiedTextFromHtml(clipboardData.getData('text/html')).length === 0;
}
